import { Button, Col, Form, message, Row, Popover, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo, useRef, useState } from "react";
import API, { apis } from "common/apis";
import { IUserTableRackMappingUser } from "common/interface";
import { clearUserTableRackMappings, getUserTableRackMappings } from "store/user-table-rack-mapping/userTableRackMappingSlice";
import { getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";

type UserField = "runners" | "pickers" | "packers" | "dispatchers" | "helpers";

type TableMapping = {
  key: string;
  tableId: number;
} & Record<UserField, number[]>;

const USER_FIELDS: { field: UserField; title: string }[] = [
  { field: "runners", title: "Runner" },
  { field: "pickers", title: "Picker" },
  { field: "packers", title: "Packer" },
  { field: "helpers", title: "Helper" },
];

const PAGE_SIZE = 25;
const MAX_VISIBLE_USERS = 2;

const ViewUserTableRackMappingPage = () => {
  useDocumentTitle("Tables users mappings");
  const dispatch = useAppDispatch();
  const { darkStores, cities } = useAppSelector((state) => state.adminServices);
  const { userTableRackMappings, listStatus, listError } = useAppSelector(
    (state) => state.userTableRackMapping
  );

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [pageNo, setPageNo] = useState(1);
  const [rows, setRows] = useState<TableMapping[]>([]);
  const [storeUsers, setStoreUsers] = useState<IUserTableRackMappingUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  // Store whose users were requested; makes the users API a one-time call per store.
  const usersRequestedFor = useRef<string>();
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
  }, [dispatch]);

  // Reset page and clear stale rows in the same batch as the store change so the fetch effect
  // only ever requests page 1 of the new store, and the old store's tables can't be edited/saved.
  const onStoreChange = (value?: string) => {
    dispatch(clearUserTableRackMappings());
    setRows([]);
    setEditingKey("");
    setPageNo(1);
    setStoreUsers([]);
    usersRequestedFor.current = undefined;
    setStoreId(value);
  };

  const loadStoreUsers = async () => {
    if (!storeId || usersRequestedFor.current === storeId) return;
    usersRequestedFor.current = storeId;
    setUsersLoading(true);
    try {
      const response = await API.get(apis.getStoreTableUsers, { params: { storeId } });
      setStoreUsers(response.data ?? []);
    } catch (e) {
      usersRequestedFor.current = undefined;
      message.error("Unable to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    setEditingKey("");
    if (storeId) {
      dispatch(getUserTableRackMappings({ storeId, pageNo, pageSize: PAGE_SIZE }));
    }
  }, [dispatch, storeId, pageNo]);

  useEffect(() => {
    setRows(
      (userTableRackMappings?.data ?? []).map((item) => ({
        key: `table-${item.tableId}`,
        tableId: item.tableId,
        runners: (item.runners ?? []).map((u) => u.userId),
        pickers: (item.pickers ?? []).map((u) => u.userId),
        packers: (item.packers ?? []).map((u) => u.userId),
        dispatchers: (item.dispatchers ?? []).map((u) => u.userId),
        helpers: (item.helpers ?? []).map((u) => u.userId),
      }))
    );
  }, [userTableRackMappings]);

  useEffect(() => {
    if (listError) message.error(listError);
  }, [listError]);

  // Users already assigned on the page are included so their labels show before the users API loads.
  const userOptions = useMemo(() => {
    const map = new Map<number, string>();
    (userTableRackMappings?.data ?? []).forEach((item) =>
      USER_FIELDS.forEach(({ field }) =>
        (item[field] ?? []).forEach((u) => map.set(u.userId, u.label || `${u.phone}-${u.name}`))
      )
    );
    storeUsers.forEach((u) => map.set(u.userId, u.label || `${u.phone}-${u.name}`));
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [userTableRackMappings, storeUsers]);

  const isEditing = (record: TableMapping) => record.key === editingKey;

  const onEdit = (record: TableMapping) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const onCancel = () => {
    setEditingKey("");
    form.resetFields();
  };

  const onSave = async (record: TableMapping) => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      await API.post(apis.saveStoreTableUsers, {
        storeId: Number(storeId),
        tables: [
          {
            tableId: record.tableId,
            runners: values.runners ?? [],
            pickers: values.pickers ?? [],
            packers: values.packers ?? [],
            // Not editable here; resend as-is because the API replaces the whole table assignment.
            dispatchers: record.dispatchers,
            helpers: values.helpers ?? [],
          },
        ],
      });
      message.success(`Table ${record.tableId} updated`);
      setEditingKey("");
      dispatch(getUserTableRackMappings({ storeId: storeId as string, pageNo, pageSize: PAGE_SIZE }));
    } catch (error: any) {
      const data = error?.response?.data;
      message.error(
        data?.errors?.map((e: { message?: string }) => e.message).filter(Boolean).join(", ") ||
          data?.message ||
          "Unable to save table users"
      );
    } finally {
      setSaving(false);
    }
  };

  // Currently assigned users first; based on the value at edit start so the list doesn't jump while selecting.
  const withSelectedFirst = <T extends { value: number | string }>(options: T[], selected: (number | string)[] = []) => [
    ...options.filter((o) => selected.includes(o.value)),
    ...options.filter((o) => !selected.includes(o.value)),
  ];

  const renderCell = (field: UserField, options: { value: number | string; label: string }[]) =>
    function Cell(value: (number | string)[], record: TableMapping) {
      if (isEditing(record)) {
        return (
          <Form.Item name={field} style={{ margin: 0 }}>
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select"
              optionFilterProp="label"
              options={withSelectedFirst(options, value)}
              loading={usersLoading}
              onDropdownVisibleChange={(open) => open && loadStoreUsers()}
              maxTagCount="responsive"
              dropdownMatchSelectWidth={false}
              style={{ width: "100%" }}
            />
          </Form.Item>
        );
      }
      if (!value?.length) return <Typography.Text type="secondary">—</Typography.Text>;
      const renderUser = (v: number | string) => {
        const label = String(options.find((o) => o.value === v)?.label ?? v);
        const i = label.indexOf("-");
        const phone = i > 0 ? label.slice(0, i) : "";
        const name = i > 0 ? label.slice(i + 1) : label;
        return (
          <Tooltip key={v} title={label}>
            <div
              style={{
                background: "#f5f7fa",
                border: "1px solid #e8ebf0",
                borderRadius: 6,
                padding: "2px 8px",
                lineHeight: 1.3,
                overflow: "hidden",
              }}
            >
              <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {name}
              </div>
              {phone && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{phone}</div>}
            </div>
          </Tooltip>
        );
      };
      const extra = value.slice(MAX_VISIBLE_USERS);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {value.slice(0, MAX_VISIBLE_USERS).map(renderUser)}
          {extra.length > 0 && (
            <Popover
              trigger="click"
              title={`${extra.length} more`}
              content={<div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>{extra.map(renderUser)}</div>}
            >
              <Tag color="blue" style={{ cursor: "pointer", alignSelf: "flex-start", margin: 0 }}>
                +{extra.length} more
              </Tag>
            </Popover>
          )}
        </div>
      );
    };

  const columns: ColumnsType<TableMapping> = [
    {
      key: "tableId",
      title: "Table",
      dataIndex: "tableId",
      width: 80,
      fixed: "left",
      render: (id: number) => <Typography.Text strong>{id}</Typography.Text>,
    },
    ...USER_FIELDS.map(({ field, title }) => ({
      key: field,
      title,
      dataIndex: field,
      width: 230,
      render: renderCell(field, userOptions),
    })),
    {
      key: "action",
      title: "Action",
      width: 140,
      fixed: "right",
      render: (_, record) =>
        isEditing(record) ? (
          <Space>
            <Button type="primary" size="small" loading={saving} onClick={() => onSave(record)}>
              Save
            </Button>
            <Button size="small" disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button size="small" disabled={editingKey !== ""} onClick={() => onEdit(record)}>
            Edit
          </Button>
        ),
    },
  ];

  const filterOption = (input: string, option?: { children?: unknown }) =>
    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false;

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Tables users mappings" />
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: "1rem" }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Typography.Text strong>City</Typography.Text>
          <Select
            showSearch
            placeholder="Select city"
            style={{ width: "100%", marginTop: 4 }}
            value={cityId}
            filterOption={filterOption}
            onChange={(value) => {
              setCityId(value);
              onStoreChange(undefined);
            }}
          >
            {(cities ?? []).map((city) => (
              <Select.Option value={city.id} key={city.id}>
                {city.name} || {city.id}
              </Select.Option>
            ))}
          </Select>
        </Col>
        {cityId && (
          <Col xs={24} sm={12} md={8} lg={6}>
            <Typography.Text strong>Store ID</Typography.Text>
            <Select
              showSearch
              placeholder="Select store"
              style={{ width: "100%", marginTop: 4 }}
              value={storeId}
              filterOption={filterOption}
              onChange={onStoreChange}
            >
              {(darkStores ?? [])
                .filter((store) => store?.cityId?.toString() === cityId?.toString())
                .map((store) => (
                  <Select.Option value={store.id} key={store.id}>
                    {store.name} || {store.id}
                  </Select.Option>
                ))}
            </Select>
          </Col>
        )}
      </Row>

      {storeId ? (
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={rows}
            loading={listStatus === "loading"}
            pagination={{
              current: pageNo,
              pageSize: PAGE_SIZE,
              total: userTableRackMappings?.total ?? 0,
              showSizeChanger: false,
              onChange: setPageNo,
            }}
            scroll={{ x: 1400, y: "65vh" }}
            size="middle"
            bordered
            rowKey="key"
            rowClassName={(record) => (isEditing(record) ? "ant-table-row-selected" : "")}
          />
        </Form>
      ) : (
        <Typography.Text type="secondary">Select a city and store to view mappings.</Typography.Text>
      )}
    </>
  );
};

export default ViewUserTableRackMappingPage;
